import { useAsync, UseAsyncStatus } from '@shared/async';
import { ProtectedRoute } from '@shared/auth';
import { useErrorHandler } from '@shared/errors';
import { useHeaderDetails } from '@shared/header';
import { PaginationResult } from '@shared/pagination';
import { Box, Pagination } from 'grommet';
import React, { useEffect, useMemo, useState } from 'react';
import { HiOutlineArrowDownTray } from 'react-icons/hi2';

import { useFilters } from '@/components/filters';
import { Button } from '@/components-new/button';
import {
  ClinicalsChangeLogSearchRequest,
  useClinicalsChangeLogService
} from '@/features/changelog/api/use-clinicals-change-log-service';
import {
  ClinicalsChangeLogFilters,
  ClinicalsChangeLogSnapshotDialog,
  ClinicalsChangeLogTable
} from '@/features/changelog/components/index';
import { FilterInputs } from '@/features/changelog/components/index/clinicals-change-log-filters';
import { ClinicalsChangeLog } from '@/features/changelog/types';

const INITIAL_PAGINATION = {
  page: 1,
  total: 0,
  resultsPerPage: 25
};

const ChangelogContainer = () => {
  const { setHeaderDetails, clearHeaderDetails } = useHeaderDetails();
  const { handleError } = useErrorHandler();

  const [searchRequest, setSearchRequest] = useFilters<FilterInputs>('changelog', {
    updatedAtFrom: '',
    updatedAtUntil: '',
    states: [],
    clientIds: [],
    product: '',
    properties: [],
    // @ts-expect-error TS(2322): Type 'null' is not assignable to type 'PropertySea... Remove this comment to see the full error message
    propertySearchType: null,
    user: ''
  });
  const [pagination, setPagination] = useState(INITIAL_PAGINATION);
  const [searchResult, setSearchResult] = useState<PaginationResult<ClinicalsChangeLog>>({
    ...INITIAL_PAGINATION,
    items: []
  });

  const { exportChangeLog, fetchChangeLog } = useClinicalsChangeLogService();

  const fetchAsync = useAsync(fetchChangeLog);

  useEffect(() => {
    setHeaderDetails({
      documentTitle: 'Changelog',
      pageTitle: 'Changelog',
      breadcrumbs: []
    });
    return () => {
      clearHeaderDetails();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const request: ClinicalsChangeLogSearchRequest = {
      ...searchRequest,
      ...pagination,
      properties: searchRequest.properties ?? [],
      propertySearchType: searchRequest.propertySearchType ?? 'onlySelected',
      rpp: pagination.resultsPerPage
    };

    void fetchAsync.execute(request);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchRequest, pagination]);

  const loadingLogs = useMemo(
    () => fetchAsync.status === UseAsyncStatus.Pending,
    [fetchAsync.status]
  );

  useEffect(() => {
    const { status, error, value } = fetchAsync;

    if (status === UseAsyncStatus.Pending || status === UseAsyncStatus.Idle) return;

    if (status === UseAsyncStatus.Error) {
      handleError(error, { title: 'Failed to get Changelog', message: 'Unable to get changelog.' });
      return;
    }

    setSearchResult(value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchAsync.status]);

  const handleApplyFilters = async (filterInputs: FilterInputs) => {
    const {
      updatedAtFrom,
      updatedAtUntil,
      states,
      clientIds,
      product,
      user,
      properties,
      propertySearchType,
      ...rest
    } = filterInputs;
    setSearchRequest((current) => ({
      ...current,
      ...rest,
      // @ts-expect-error TS(2532): Object is possibly 'undefined'.
      updatedAtFrom: updatedAtFrom?.length > 0 ? updatedAtFrom : undefined,
      // @ts-expect-error TS(2532): Object is possibly 'undefined'.
      updatedAtUntil: updatedAtUntil?.length > 0 ? updatedAtUntil : undefined,
      // @ts-expect-error TS(2532): Object is possibly 'undefined'.
      states: states?.length > 0 ? states : undefined,
      // @ts-expect-error TS(2532): Object is possibly 'undefined'.
      clientIds: clientIds?.length > 0 ? clientIds : undefined,
      // @ts-expect-error TS(2532): Object is possibly 'undefined'.
      product: product?.length > 0 ? product : undefined,
      // @ts-expect-error TS(2532): Object is possibly 'undefined'.
      user: user?.length > 0 ? user: undefined,
      // @ts-expect-error TS(2532): Object is possibly 'undefined'.
      properties: properties?.length > 0 ? properties : undefined,
      propertySearchType
    }));
    setPagination((current) => ({
      ...current,
      page: 1
    }));
  };

  const handlePaginationChange = ({ page }: { page: number }) => {
    setPagination((current) => ({
      ...current,
      page
    }));
  };

  const [viewingSnapshot, setViewingSnapshot] = useState<ClinicalsChangeLog>();

  const handleViewSnapshot = (row: ClinicalsChangeLog) => setViewingSnapshot(row);

  const handleCloseSnapshot = () => setViewingSnapshot(undefined);

  const handleExport = async (filters: FilterInputs) => {
    const {
      updatedAtFrom,
      updatedAtUntil,
      states,
      clientIds,
      product,
      user,
      properties,
      propertySearchType
    } = filters;

    try {
      const request = {
        // @ts-expect-error TS(2532): Object is possibly 'undefined'.
        updatedAtFrom: updatedAtFrom?.length > 0 ? updatedAtFrom : undefined,
        // @ts-expect-error TS(2532): Object is possibly 'undefined'.
        updatedAtUntil: updatedAtUntil?.length > 0 ? updatedAtUntil : undefined,
        // @ts-expect-error TS(2532): Object is possibly 'undefined'.
        states: states?.length > 0 ? states : undefined,
        // @ts-expect-error TS(2532): Object is possibly 'undefined'.
        clientIds: clientIds?.length > 0 ? clientIds : undefined,
        // @ts-expect-error TS(2532): Object is possibly 'undefined'.
        product: product?.length > 0 ? product : undefined,
        // @ts-expect-error TS(2532): Object is possibly 'undefined'.
        user: user?.length > 0 ? user: undefined,
        propertySearchType: propertySearchType ?? 'onlySelected',
      };

      const requestProperties = properties != null && properties.length > 0 ? properties : undefined;

      const file = await exportChangeLog({
        ...request,
        properties: requestProperties
      });

      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(file.contents);
      link.download = file.filename;
      link.click();
    } catch (ex) {
      handleError(ex as Error, { title: 'Failed to Export', message: 'Unable to export changelogs.' });
    }
  };

  return <>
    <ClinicalsChangeLogFilters
      disabled={loadingLogs}
      defaultFilters={searchRequest}
      onApplyFilters={handleApplyFilters}
    >
      <Button
        aria-label="Export to CSV"
        title="Export to CSV"
        onClick={() => handleExport(searchRequest)}
      >
        <HiOutlineArrowDownTray/>
      </Button>
    </ClinicalsChangeLogFilters>

    <ClinicalsChangeLogTable data={searchResult.items} loading={loadingLogs} onViewSnapshot={handleViewSnapshot}/>
    {searchResult?.total > 0 && <Box direction="row" justify="center" pad={{ top: '20px' }}>
      <Pagination
        size="medium"
        page={searchResult.page}
        step={searchResult.resultsPerPage}
        numberItems={searchResult.total}
        onChange={handlePaginationChange}
      />
    </Box>}

    <ClinicalsChangeLogSnapshotDialog
      open={!!viewingSnapshot}
      row={viewingSnapshot}
      onClose={handleCloseSnapshot}
    />
  </>;
};

const Changelog = () => {
  return <ProtectedRoute component={ChangelogContainer} policies={['isAnyArtiaUser']}/>;
};

export default Changelog;
